/**
 * Repositorio de produccion contra Supabase (secciones 12 y 13).
 *
 * Reparto de responsabilidades:
 *   - Las LECTURAS van directas a PostgREST y las filtra la seguridad a nivel
 *     de fila (RLS). Lo que un usuario no puede ver, no se lo devuelve la base
 *     de datos, no la interfaz.
 *   - Las ESCRITURAS que tocan puntuacion, estado o rol pasan por Edge
 *     Functions con privilegios (RNF-06): `final_score`, `status` y
 *     `reviewed_by` no son escribibles desde el cliente bajo ninguna clave.
 */

import { CATEGORY_RULES } from '@/config/categories';
import { summarizeReactions } from '@/lib/social';
import { assignWeightClass } from '@/lib/categories';
import type {
  AdminMetrics,
  AuditLogEntry,
  Badge,
  Competition,
  Enrollment,
  Exercise,
  ForumPost,
  Language,
  Profile,
  PublicProfile,
  PublicSubmission,
  ReactionKind,
  SubmissionComment,
  Report,
  Season,
  Submission,
  UserBadge,
} from '@/types/domain';
import {
  RepositoryError,
  type AdminSubmissionFilter,
  type AdminUserUpdate,
  type AuthSession,
  type CompetitionFilter,
  type ContactMessageInput,
  type CreateProfileInput,
  type ProfileStats,
  type RankingQuery,
  type RankingResult,
  type RejectSubmissionInput,
  type Repository,
  type ScoreSubmissionInput,
  type SignUpInput,
  type StandingsGroup,
  type StandingsQuery,
  type SubmissionDetail,
  type SubmitMarkInput,
  type UpdateProfileInput,
  type UpsertCompetitionInput,
  type UpsertExerciseInput,
  type UpsertSeasonInput,
  type WeightClassThresholds,
} from '../types';
import { getSupabase, invokeFunction } from './client';

/** Forma de un uuid, para saber si un identificador de ruta es un id o un slug. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Traduce el error de PostgREST a un codigo con traduccion en la interfaz.
 *
 * Cuidado con 23505: es "valor duplicado" a secas, y en esta tabla puede
 * saltar tanto por el alias como por la clave primaria. Traducirlo siempre a
 * "alias en uso" mandaba al usuario a cambiar un alias que estaba libre
 * mientras el problema real era otro. Solo se llama alias cuando el indice que
 * salta es el del alias.
 */
function fail(
  error: { message?: string; code?: string; details?: string } | null,
  fallback: string,
): never {
  if (error?.code === '23505') {
    const detalle = `${error.message ?? ''} ${error.details ?? ''}`;
    if (/alias/i.test(detalle)) throw new RepositoryError('aliasTaken');
    throw new RepositoryError(fallback, { detail: detalle.trim() });
  }
  // Un hueco o una regla incumplida no son "algo ha salido mal": se nombra el
  // campo o la regla, que es lo que hace falta para corregirlo.
  if (error?.code === '23502') {
    const field = /column "([^"]+)"/.exec(error.message ?? '')?.[1] ?? '';
    throw new RepositoryError('missingField', { field, detail: error.message ?? '' });
  }
  // Los periodos de espera del servidor llegan como check_violation con el
  // nombre de la regla. Sin esto salian como "un dato no cumple las reglas",
  // que no dice cual es la regla ni cuando se puede volver a cambiar.
  const rule = `${error?.message ?? ''} ${error?.details ?? ''}`;
  // Limites antispam de los comentarios (migracion 20260101001500).
  if (/comment_rate_limit/.test(rule)) throw new RepositoryError('commentRateLimit');
  if (/comment_daily_limit/.test(rule)) throw new RepositoryError('commentDailyLimit');
  // Borrado de una competicion con envios (migracion 20260101001700).
  if (/competition_has_submissions/.test(rule)) {
    throw new RepositoryError('competitionHasSubmissions');
  }
  if (/weight_cooldown/.test(rule)) {
    throw new RepositoryError('weightCooldown', {
      days: CATEGORY_RULES.WEIGHT_CHANGE_COOLDOWN_DAYS,
    });
  }
  if (/location_cooldown/.test(rule)) {
    throw new RepositoryError('locationCooldown', {
      days: CATEGORY_RULES.LOCATION_CHANGE_COOLDOWN_DAYS,
    });
  }
  if (error?.code === '23514') {
    const constraint = /constraint "([^"]+)"/.exec(error.message ?? '')?.[1] ?? '';
    throw new RepositoryError('invalidData', { constraint, detail: error.message ?? '' });
  }
  if (error?.code === '42501') throw new RepositoryError('forbidden');
  throw new RepositoryError(fallback, { detail: error?.message ?? '' });
}

/**
 * Columnas publicas de una marca: ni peso corporal, ni desglose, ni notas de
 * moderacion. Las reacciones y los comentarios vienen incrustados para no
 * hacer tres viajes por cada tarjeta.
 */
const PUBLIC_SUBMISSION_SELECT =
  'id, competition_id, user_id, exercise_id, platform, post_url, final_score, created_at,' +
  ' hidden_at,' +
  ' athlete:public_profiles!submissions_user_id_fkey(alias, rank_level),' +
  ' submission_reactions(user_id, kind), submission_comments(id)';

/** El tablon trae de una vez mensajes y respuestas; mas no cabe en pantalla. */
const FORUM_PAGE_SIZE = 300;

const FORUM_POST_SELECT =
  'id, parent_id, author_id, body, hidden_at, created_at,' +
  ' author:public_profiles!forum_posts_author_id_fkey(alias, rank_level)';

const COMMENT_SELECT =
  'id, submission_id, author_id, body, hidden_at, created_at,' +
  ' author:public_profiles!submission_comments_author_id_fkey(alias, rank_level)';

/* eslint-disable @typescript-eslint/no-explicit-any */
function toPublicSubmission(row: any, me: string | null): PublicSubmission {
  const { counts, mine } = summarizeReactions(row.submission_reactions ?? [], me);
  return {
    id: row.id,
    competition_id: row.competition_id,
    user_id: row.user_id,
    alias: row.athlete?.alias ?? '',
    rank_level: row.athlete?.rank_level ?? 1,
    exercise_id: row.exercise_id,
    platform: row.platform,
    post_url: row.post_url,
    final_score: row.final_score ?? 0,
    created_at: row.created_at,
    hidden: Boolean(row.hidden_at),
    reactions: counts,
    my_reaction: mine,
    comment_count: (row.submission_comments ?? []).length,
  };
}

function toForumPost(row: any): ForumPost {
  return {
    id: row.id,
    parent_id: row.parent_id,
    author_id: row.author_id,
    author_alias: row.author?.alias ?? '',
    author_rank_level: row.author?.rank_level ?? 1,
    body: row.body,
    hidden: Boolean(row.hidden_at),
    created_at: row.created_at,
  };
}

function toComment(row: any): SubmissionComment {
  return {
    id: row.id,
    submission_id: row.submission_id,
    author_id: row.author_id,
    author_alias: row.author?.alias ?? '',
    author_rank_level: row.author?.rank_level ?? 1,
    body: row.body,
    hidden: Boolean(row.hidden_at),
    created_at: row.created_at,
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export class SupabaseRepository implements Repository {
  readonly isDemo = false;

  // --- Autenticacion (5.1) -------------------------------------------------

  async getSession(): Promise<AuthSession | null> {
    const supabase = getSupabase();
    const { data } = await supabase.auth.getUser();
    const user = data.user;
    if (!user) return null;

    const { data: profile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    return {
      user: {
        id: user.id,
        email: user.email ?? '',
        emailVerified: Boolean(user.email_confirmed_at),
        // Supabase guarda el proveedor en app_metadata; sin el, correo.
        provider: user.app_metadata?.provider === 'google' ? 'GOOGLE' : 'EMAIL',
      },
      profile: (profile as Profile | null) ?? null,
    };
  }

  onAuthChange(listener: (session: AuthSession | null) => void): () => void {
    const { data } = getSupabase().auth.onAuthStateChange(() => {
      void this.getSession().then(listener);
    });
    return () => data.subscription.unsubscribe();
  }

  async signUpWithEmail(input: SignUpInput): Promise<AuthSession> {
    if (!input.acceptedTerms) throw new RepositoryError('mustAcceptTerms');
    if (!input.confirmedAge) throw new RepositoryError('mustConfirmAge');

    const { error } = await getSupabase().auth.signUp({
      email: input.email,
      password: input.password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        // Queda registrada la aceptacion, exigida por RF-AUTH-10.
        data: { accepted_terms_at: new Date().toISOString(), confirmed_age: true },
      },
    });
    if (error) fail(error, 'generic');

    const session = await this.getSession();
    if (!session) throw new RepositoryError('generic');
    return session;
  }

  async signInWithEmail(email: string, password: string): Promise<AuthSession> {
    const { error } = await getSupabase().auth.signInWithPassword({ email, password });
    if (error) {
      // El bloqueo progresivo tras 5 intentos lo aplica Supabase Auth
      // (RF-AUTH-09) y devuelve 429.
      if (error.status === 429) throw new RepositoryError('tooManyAttempts', { minutes: 15 });
      throw new RepositoryError('invalidCredentials');
    }
    const session = await this.getSession();
    if (!session) throw new RepositoryError('invalidCredentials');
    return session;
  }

  async signInWithGoogle(redirectTo: string): Promise<AuthSession | null> {
    const { error } = await getSupabase().auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) fail(error, 'generic');
    // A partir de aqui el navegador se va a Google. Devolvemos null para que
    // quien llame no intente navegar sobre una pagina que ya se esta yendo; la
    // sesion se recoge al volver, en `getSession`.
    return null;
  }

  async signOut(): Promise<void> {
    await getSupabase().auth.signOut({ scope: 'local' });
  }

  async signOutEverywhere(): Promise<void> {
    await getSupabase().auth.signOut({ scope: 'global' });
  }

  async requestPasswordReset(email: string): Promise<void> {
    // Token de un solo uso valido 60 minutos (RF-AUTH-07); la caducidad se
    // configura en el panel de Supabase.
    const { error } = await getSupabase().auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset`,
    });
    if (error) fail(error, 'generic');
  }

  async resendVerificationEmail(): Promise<void> {
    const { data } = await getSupabase().auth.getUser();
    if (!data.user?.email) throw new RepositoryError('forbidden');
    const { error } = await getSupabase().auth.resend({
      type: 'signup',
      email: data.user.email,
    });
    if (error) fail(error, 'generic');
  }

  // --- Perfil (5.3 / 9.4) --------------------------------------------------

  private async requireUserId(): Promise<string> {
    const { data } = await getSupabase().auth.getUser();
    if (!data.user) throw new RepositoryError('forbidden');
    return data.user.id;
  }

  async createProfile(input: CreateProfileInput): Promise<Profile> {
    const userId = await this.requireUserId();

    // `upsert`, no `insert`. La fila ya existe: la crea un disparador sobre
    // `auth.users` en cuanto alguien se da de alta (migracion 20260101000800),
    // asi que insertar de nuevo choca contra la clave primaria. El onboarding
    // no crea el perfil, lo COMPLETA.
    //
    // `weight_updated_at` y `location_updated_at` NO se mandan desde aqui: las
    // pone el disparador del servidor, y solo cuando el dato cambia de verdad.
    // Mandarlas en cada guardado reiniciaba el periodo de espera sin motivo.
    const { data, error } = await getSupabase()
      .from('profiles')
      .upsert({
        id: userId,
        alias: input.alias.trim(),
        avatar_url: input.avatarUrl,
        language: input.language,
        country: input.country,
        city: input.city,
        birth_date: input.birthDate,
        gender_category: input.genderCategory,
        body_weight_kg: input.bodyWeightKg,
        weight_class: assignWeightClass(input.bodyWeightKg, input.genderCategory),
        onboarding_completed: true,
      }, { onConflict: 'id' })
      .select()
      .single();

    if (error) fail(error, 'generic');
    return data as Profile;
  }

  async getMyProfile(): Promise<Profile | null> {
    const { data } = await getSupabase().auth.getUser();
    if (!data.user) return null;
    const { data: profile } = await getSupabase()
      .from('profiles')
      .select('*')
      .eq('id', data.user.id)
      .maybeSingle();
    return (profile as Profile | null) ?? null;
  }

  async updateMyProfile(input: UpdateProfileInput): Promise<Profile> {
    // Los periodos de espera de peso y ubicacion se comprueban tambien en un
    // trigger de la base de datos, para que no dependan del cliente.
    const userId = await this.requireUserId();
    const patch: Record<string, unknown> = {};

    if (input.alias !== undefined) patch.alias = input.alias.trim();
    if (input.avatarUrl !== undefined) patch.avatar_url = input.avatarUrl;
    if (input.language !== undefined) patch.language = input.language;
    if (input.privacyLevel !== undefined) patch.privacy_level = input.privacyLevel;
    if (input.soundsEnabled !== undefined) patch.sounds_enabled = input.soundsEnabled;
    if (input.emailCompetitions !== undefined) {
      patch.email_competitions = input.emailCompetitions;
    }
    if (input.onboardingCompleted !== undefined) {
      patch.onboarding_completed = input.onboardingCompleted;
    }
    if (input.bodyWeightKg !== undefined) patch.body_weight_kg = input.bodyWeightKg;
    if (input.country !== undefined) patch.country = input.country;
    if (input.city !== undefined) patch.city = input.city;

    const { data, error } = await getSupabase()
      .from('profiles')
      .update(patch)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      // Los triggers avisan con estos mensajes cuando el periodo no ha pasado.
      if (error.message?.includes('weight_cooldown')) throw new RepositoryError('weightCooldown');
      if (error.message?.includes('location_cooldown')) {
        throw new RepositoryError('locationCooldown');
      }
      fail(error, 'generic');
    }
    return data as Profile;
  }

  async isAliasAvailable(alias: string): Promise<boolean> {
    // El propio perfil no cuenta: al editar el suyo sin cambiar el alias, el
    // usuario se encontraria con que su nombre "ya esta en uso".
    const userId = await this.currentUserId();

    let query = getSupabase()
      .from('public_profiles')
      .select('id', { count: 'exact', head: true })
      .ilike('alias', alias.trim());

    if (userId) query = query.neq('id', userId);

    const { count } = await query;
    return (count ?? 0) === 0;
  }

  /** Identificador del usuario actual, o null si no hay sesion. */
  private async currentUserId(): Promise<string | null> {
    const { data } = await getSupabase().auth.getUser();
    return data.user?.id ?? null;
  }

  async getPublicProfile(alias: string): Promise<PublicProfile | null> {
    const { data } = await getSupabase()
      .from('public_profiles')
      .select('*')
      .ilike('alias', alias.trim())
      .maybeSingle();
    return (data as PublicProfile | null) ?? null;
  }

  async getProfileStats(userId: string): Promise<ProfileStats> {
    // Vista materializada con las estadisticas ya agregadas, para no hacer
    // cuatro consultas por perfil.
    const { data, error } = await getSupabase().rpc('get_profile_stats', { p_user_id: userId });
    if (error) fail(error, 'generic');
    return data as ProfileStats;
  }

  async searchProfiles(query: string, limit = 10): Promise<PublicProfile[]> {
    const { data } = await getSupabase()
      .from('public_profiles')
      .select('*')
      .ilike('alias', `%${query.trim()}%`)
      .limit(limit);
    return (data as PublicProfile[] | null) ?? [];
  }

  async exportMyData(): Promise<Record<string, unknown>> {
    // RF-AUTH-11 / RF-LEG-02: exportacion en autoservicio.
    return invokeFunction<Record<string, unknown>>('export-my-data');
  }

  async deleteMyAccount(): Promise<void> {
    await invokeFunction('delete-my-account');
    await this.signOut();
  }

  // --- Temporadas (6.1) ----------------------------------------------------

  async getCurrentSeason(): Promise<Season | null> {
    const { data } = await getSupabase()
      .from('seasons')
      .select('*')
      .eq('status', 'ACTIVE')
      .maybeSingle();
    return (data as Season | null) ?? null;
  }

  async listSeasons(): Promise<Season[]> {
    const { data } = await getSupabase()
      .from('seasons')
      .select('*')
      .order('start_date', { ascending: false });
    return (data as Season[] | null) ?? [];
  }

  // --- Competiciones (6.2) -------------------------------------------------

  async listCompetitions(filter: CompetitionFilter = {}): Promise<Competition[]> {
    let query = getSupabase().from('competitions').select('*');

    if (filter.status === 'ACTIVE_OR_SCHEDULED') {
      query = query.in('status', ['ACTIVE', 'SCHEDULED']);
    } else if (filter.status) {
      query = query.eq('status', filter.status);
    }
    if (filter.seasonId) query = query.eq('season_id', filter.seasonId);
    if (filter.theme) query = query.eq('theme', filter.theme);

    query = query.order('starts_at', { ascending: false });
    if (filter.limit) query = query.limit(filter.limit);

    const { data, error } = await query;
    if (error) fail(error, 'generic');
    return (data as Competition[] | null) ?? [];
  }

  async getCompetition(idOrSlug: string): Promise<Competition | null> {
    // Por id o por slug, nunca los dos en un `or`. `id` es uuid: con
    // `id.eq.dominadas-estrictas-...` Postgres no puede convertir el texto y
    // rechaza la consulta ENTERA con un 400. Como el error se ignoraba, la
    // ficha de cualquier competicion abierta por su enlace volvia "no existe"
    // y la pagina devolvia al atleta a la lista: no habia forma de
    // inscribirse. En la demo no pasaba porque alli se busca en memoria.
    const column = UUID_PATTERN.test(idOrSlug) ? 'id' : 'slug';
    const { data, error } = await getSupabase()
      .from('competitions')
      .select('*')
      .eq(column, idOrSlug)
      .maybeSingle();
    if (error) fail(error, 'generic');
    return (data as Competition | null) ?? null;
  }

  async enroll(competitionId: string): Promise<Enrollment> {
    // La funcion SQL asigna la categoria en servidor a partir del perfil, para
    // que el cliente no pueda elegirse la suya (RF-CAT-02), y comprueba el
    // plazo de cierre en la misma transaccion.
    const { data, error } = await getSupabase().rpc('enroll_in_competition', {
      p_competition_id: competitionId,
    });
    if (error) {
      if (error.message?.includes('DEADLINE_PASSED')) {
        throw new RepositoryError('submission.DEADLINE_PASSED');
      }
      fail(error, 'generic');
    }
    return data as Enrollment;
  }

  async listMyEnrollments(): Promise<Enrollment[]> {
    const userId = await this.requireUserId();
    const { data } = await getSupabase().from('enrollments').select('*').eq('user_id', userId);
    return (data as Enrollment[] | null) ?? [];
  }

  async getStandings(query: StandingsQuery): Promise<StandingsGroup[]> {
    const { data, error } = await getSupabase().rpc('get_competition_standings', {
      p_competition_id: query.competitionId,
      p_gender: query.genderCategory === 'ALL' ? null : (query.genderCategory ?? null),
      p_weight_class: query.weightClass === 'ALL' ? null : (query.weightClass ?? null),
    });
    if (error) fail(error, 'generic');
    return (data as StandingsGroup[] | null) ?? [];
  }

  // --- Ejercicios (8.2) ----------------------------------------------------

  async listExercises(): Promise<Exercise[]> {
    const { data } = await getSupabase()
      .from('exercises')
      .select('*')
      .eq('active', true)
      .order('difficulty_coefficient');
    return (data as Exercise[] | null) ?? [];
  }

  // --- Envios (7) ----------------------------------------------------------

  async submitMark(input: SubmitMarkInput): Promise<Submission> {
    // Toda la validacion ocurre en la Edge Function: normaliza el enlace,
    // comprueba que el post es publico y detecta duplicados. El cliente no
    // decide nada de eso (RNF-06). Ya no puntua: sin marca declarada no hay
    // nada que calcular hasta que el revisor vea el video.
    return invokeFunction<Submission>('score-submission', {
      competitionId: input.competitionId,
      postUrl: input.postUrl,
      notes: input.notes,
    });
  }

  async listMySubmissions(): Promise<Submission[]> {
    const userId = await this.requireUserId();
    const { data } = await getSupabase()
      .from('submissions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return (data as Submission[] | null) ?? [];
  }

  async getMySubmissionFor(competitionId: string): Promise<Submission | null> {
    const userId = await this.requireUserId();
    const { data } = await getSupabase()
      .from('submissions')
      .select('*')
      .eq('user_id', userId)
      .eq('competition_id', competitionId)
      .maybeSingle();
    return (data as Submission | null) ?? null;
  }

  async deleteSubmission(submissionId: string): Promise<void> {
    // La RLS solo permite borrar los propios y en estado PENDIENTE
    // (RF-VID-09).
    const { error } = await getSupabase().from('submissions').delete().eq('id', submissionId);
    if (error) fail(error, 'generic');
  }

  // --- Rankings (9.3) ------------------------------------------------------

  async getRanking(query: RankingQuery): Promise<RankingResult> {
    const { data, error } = await getSupabase().rpc('get_ranking', {
      p_scope: query.scope,
      p_scope_key: query.scopeKey ?? null,
      p_season_id: query.seasonId ?? null,
      p_gender: query.genderCategory === 'ALL' ? null : (query.genderCategory ?? null),
      p_weight_class: query.weightClass === 'ALL' ? null : (query.weightClass ?? null),
      p_theme: query.theme === 'ALL' ? null : (query.theme ?? null),
      p_rank_level: query.rankLevel === 'ALL' ? null : (query.rankLevel ?? null),
      p_period: query.period ?? 'SEASON',
      p_search: query.search?.trim() || null,
      p_page: query.page ?? 1,
      p_page_size: query.pageSize ?? 25,
    });
    if (error) fail(error, 'generic');
    return data as RankingResult;
  }

  // --- Insignias (9.2) -----------------------------------------------------

  async listBadges(): Promise<Badge[]> {
    const { data } = await getSupabase().from('badges').select('*');
    return (data as Badge[] | null) ?? [];
  }

  async listUserBadges(userId: string): Promise<UserBadge[]> {
    const { data } = await getSupabase()
      .from('user_badges')
      .select('*')
      .eq('user_id', userId)
      .order('awarded_at', { ascending: false });
    return (data as UserBadge[] | null) ?? [];
  }

  async setBadgeFeatured(userBadgeId: string, featured: boolean): Promise<void> {
    // El tope de 3 destacadas lo impone un trigger (RF-INS-02).
    const { error } = await getSupabase()
      .from('user_badges')
      .update({ featured })
      .eq('id', userBadgeId);
    if (error) {
      if (error.message?.includes('max_featured')) throw new RepositoryError('maxFeatured');
      fail(error, 'generic');
    }
  }

  // --- Social (9.4) --------------------------------------------------------

  async follow(userId: string): Promise<void> {
    const me = await this.requireUserId();
    const { error } = await getSupabase()
      .from('follows')
      .insert({ follower_id: me, followed_id: userId });
    if (error && error.code !== '23505') fail(error, 'generic');
  }

  async unfollow(userId: string): Promise<void> {
    const me = await this.requireUserId();
    const { error } = await getSupabase()
      .from('follows')
      .delete()
      .eq('follower_id', me)
      .eq('followed_id', userId);
    if (error) fail(error, 'generic');
  }

  async isFollowing(userId: string): Promise<boolean> {
    const { data } = await getSupabase().auth.getUser();
    if (!data.user) return false;
    const { count } = await getSupabase()
      .from('follows')
      .select('id', { count: 'exact', head: true })
      .eq('follower_id', data.user.id)
      .eq('followed_id', userId);
    return (count ?? 0) > 0;
  }

  async createReport(
    targetType: Report['target_type'],
    targetId: string,
    reason: string,
  ): Promise<void> {
    const me = await this.requireUserId();
    const { error } = await getSupabase().from('reports').insert({
      reporter_id: me,
      target_type: targetType,
      target_id: targetId,
      reason,
      status: 'OPEN',
    });
    if (error) fail(error, 'generic');
  }

  // --- Reacciones y comentarios ---------------------------------------------

  async listCompetitionSubmissions(competitionId: string): Promise<PublicSubmission[]> {
    const me = await this.currentUserId();
    const { data, error } = await getSupabase()
      .from('submissions')
      .select(PUBLIC_SUBMISSION_SELECT)
      .eq('competition_id', competitionId)
      .eq('status', 'APROBADO')
      .order('final_score', { ascending: false });

    if (error) fail(error, 'generic');
    return (data ?? []).map((row) => toPublicSubmission(row, me));
  }

  async getPublicSubmission(submissionId: string): Promise<PublicSubmission | null> {
    const me = await this.currentUserId();
    const { data, error } = await getSupabase()
      .from('submissions')
      .select(PUBLIC_SUBMISSION_SELECT)
      .eq('id', submissionId)
      .eq('status', 'APROBADO')
      .maybeSingle();

    if (error) fail(error, 'generic');
    return data ? toPublicSubmission(data, me) : null;
  }

  async setReaction(submissionId: string, kind: ReactionKind | null): Promise<void> {
    const me = await this.requireUserId();

    if (kind === null) {
      const { error } = await getSupabase()
        .from('submission_reactions')
        .delete()
        .eq('submission_id', submissionId)
        .eq('user_id', me);
      if (error) fail(error, 'generic');
      return;
    }

    // Una reaccion por persona y envio: cambiarla sustituye a la anterior.
    const { error } = await getSupabase()
      .from('submission_reactions')
      .upsert(
        { submission_id: submissionId, user_id: me, kind },
        { onConflict: 'submission_id,user_id' },
      );
    if (error) fail(error, 'generic');
  }

  async listComments(submissionId: string): Promise<SubmissionComment[]> {
    const { data, error } = await getSupabase()
      .from('submission_comments')
      .select(COMMENT_SELECT)
      .eq('submission_id', submissionId)
      .order('created_at', { ascending: true });

    if (error) fail(error, 'generic');
    return (data ?? []).map(toComment);
  }

  async addComment(submissionId: string, body: string): Promise<SubmissionComment> {
    const me = await this.requireUserId();
    const { data, error } = await getSupabase()
      .from('submission_comments')
      .insert({ submission_id: submissionId, author_id: me, body: body.trim() })
      .select(COMMENT_SELECT)
      .single();

    if (error) fail(error, 'generic');
    return toComment(data);
  }

  async deleteComment(commentId: string): Promise<void> {
    // Quien puede borrarlo lo decide la RLS: su autor o moderacion.
    const { error } = await getSupabase().from('submission_comments').delete().eq('id', commentId);
    if (error) fail(error, 'generic');
  }

  // --- Foro de la comunidad -------------------------------------------------

  /**
   * El tablon entero en una sola consulta: mensajes y respuestas juntos.
   *
   * Pedirlos por separado —los mensajes y luego las respuestas de cada uno—
   * serian tantas consultas como mensajes hay en pantalla. Como una respuesta
   * nunca cuelga de otra respuesta, con `parent_id` basta para rehacer el
   * arbol en el navegador.
   *
   * Lo oculto por moderacion no llega aqui: lo filtra la politica de la tabla
   * (migracion 20260101002000), que si se lo ensena a su autor y a moderacion.
   */
  async listForumPosts(): Promise<ForumPost[]> {
    const { data, error } = await getSupabase()
      .from('forum_posts')
      .select(FORUM_POST_SELECT)
      .order('created_at', { ascending: false })
      .limit(FORUM_PAGE_SIZE);

    if (error) fail(error, 'generic');
    return (data ?? []).map(toForumPost);
  }

  async addForumPost(body: string, parentId?: string | null): Promise<ForumPost> {
    const me = await this.requireUserId();
    const { data, error } = await getSupabase()
      .from('forum_posts')
      .insert({ author_id: me, parent_id: parentId ?? null, body: body.trim() })
      .select(FORUM_POST_SELECT)
      .single();

    if (error) fail(error, 'generic');
    return toForumPost(data);
  }

  async deleteForumPost(postId: string): Promise<void> {
    const me = await this.requireUserId();
    const { error } = await getSupabase()
      .from('forum_posts')
      .delete()
      .eq('id', postId)
      .eq('author_id', me);

    if (error) fail(error, 'generic');
  }

  // --- Contacto ------------------------------------------------------------

  async sendContactMessage(input: ContactMessageInput): Promise<void> {
    // Pasa por la funcion `contact-message`: valida, limita los envios por IP,
    // guarda con la clave de servicio y avisa por correo. La tabla no admite
    // inserciones directas desde el navegador.
    await invokeFunction<{ ok: true }>('contact-message', { ...input });
  }

  // --- Integraciones sociales (7.3) ----------------------------------------

  async connectSocialAccount(
    platform: 'INSTAGRAM' | 'TIKTOK',
    redirectTo: string,
  ): Promise<void> {
    const { url } = await invokeFunction<{ url: string }>('social-connect', {
      platform,
      redirectTo,
    });
    window.location.assign(url);
  }

  async disconnectSocialAccount(platform: 'INSTAGRAM' | 'TIKTOK'): Promise<void> {
    const userId = await this.requireUserId();
    const column = platform === 'INSTAGRAM' ? 'instagram_account_id' : 'tiktok_account_id';
    const { error } = await getSupabase()
      .from('profiles')
      .update({ [column]: null })
      .eq('id', userId);
    if (error) fail(error, 'generic');
  }

  async listAutoDetectedDrafts(): Promise<Submission[]> {
    const userId = await this.requireUserId();
    const { data } = await getSupabase()
      .from('submissions')
      .select('*')
      .eq('user_id', userId)
      .eq('auto_detected', true)
      .eq('status', 'BORRADOR');
    return (data as Submission[] | null) ?? [];
  }

  // --- Administracion (10) -------------------------------------------------

  async adminListSubmissions(filter: AdminSubmissionFilter = {}): Promise<SubmissionDetail[]> {
    // `public_profiles!submissions_user_id_fkey`, no `public_profiles` a secas.
    // `submissions` tiene DOS claves foraneas a perfiles —quien envia el video
    // y quien lo revisa— y sin decir cual PostgREST no elige: devuelve un
    // error de relacion ambigua y la cola de revision se queda vacia.
    let query = getSupabase()
      .from('submissions')
      .select(
        '*, athlete:public_profiles!submissions_user_id_fkey(*),' +
          ' competition:competitions(*), exercise:exercises(*)',
      );

    if (filter.status === 'PENDING_ANY') query = query.in('status', ['PENDIENTE', 'EN_REVISION']);
    else if (filter.status) query = query.eq('status', filter.status);
    if (filter.competitionId) query = query.eq('competition_id', filter.competitionId);
    if (filter.genderCategory) query = query.eq('gender_category', filter.genderCategory);
    if (filter.weightClass) query = query.eq('weight_class', filter.weightClass);

    query = query.order('created_at', { ascending: true }).limit(filter.limit ?? 100);

    const { data, error } = await query;
    // Fallback generico, no 'forbidden': `fail` ya traduce el 42501 real. Con
    // 'forbidden' aqui, un error de consulta se presentaba como un problema de
    // permisos y mandaba a buscar el fallo donde no estaba.
    if (error) fail(error, 'generic');

    return ((data ?? []) as unknown[]).map((row) => {
      const record = row as Record<string, unknown>;
      const { athlete, competition, exercise, ...submission } = record;
      return {
        submission: submission as unknown as Submission,
        athlete: athlete as PublicProfile,
        competition: competition as Competition,
        exercise: exercise as Exercise,
      };
    });
  }

  async adminGetSubmission(submissionId: string): Promise<SubmissionDetail | null> {
    const list = await this.adminListSubmissions({ limit: 1 });
    return list.find((d) => d.submission.id === submissionId) ?? null;
  }

  async adminScoreSubmission(input: ScoreSubmissionInput): Promise<Submission> {
    return invokeFunction<Submission>('admin-score-submission', {
      ...input,
    } as unknown as Record<string, unknown>);
  }

  async adminRejectSubmission(input: RejectSubmissionInput): Promise<Submission> {
    return invokeFunction<Submission>('admin-reject-submission', {
      ...input,
    } as unknown as Record<string, unknown>);
  }

  async adminFlagForSecondOpinion(submissionId: string, note: string): Promise<Submission> {
    return invokeFunction<Submission>('admin-flag-submission', { submissionId, note });
  }

  async adminUpsertCompetition(input: UpsertCompetitionInput): Promise<Competition> {
    const supabase = getSupabase();
    const { data, error } = input.id
      ? await supabase.from('competitions').update(input).eq('id', input.id).select().single()
      : await supabase.from('competitions').insert(input).select().single();
    if (error) fail(error, 'generic');
    return data as Competition;
  }

  async adminDeleteCompetition(competitionId: string): Promise<void> {
    // Que no tenga envios lo comprueba la base (migracion 20260101001700), no
    // esta pantalla: entre mirar y borrar puede entrar uno.
    const { data, error } = await getSupabase()
      .from('competitions')
      .delete()
      .eq('id', competitionId)
      .select('id');
    if (error) fail(error, 'generic');
    // Sin error y sin filas: la RLS no ha dejado borrarla (no es administrador)
    // o ya no existia.
    if (!data || data.length === 0) throw new RepositoryError('forbidden');
  }

  async adminPublishResults(competitionId: string): Promise<void> {
    // Consolida puntuaciones, resuelve podios por categoria, otorga insignias
    // y actualiza rangos y rankings en una sola transaccion (RF-ADM-10).
    await invokeFunction('publish-results', { competitionId });
  }

  async adminUpsertExercise(input: UpsertExerciseInput): Promise<Exercise> {
    const supabase = getSupabase();
    const { data, error } = input.id
      ? await supabase.from('exercises').update(input).eq('id', input.id).select().single()
      : await supabase.from('exercises').insert(input).select().single();
    if (error) fail(error, 'generic');
    return data as Exercise;
  }

  async adminUpsertSeason(input: UpsertSeasonInput): Promise<Season> {
    const supabase = getSupabase();
    const { data, error } = input.id
      ? await supabase.from('seasons').update(input).eq('id', input.id).select().single()
      : await supabase.from('seasons').insert(input).select().single();
    if (error) fail(error, 'generic');
    return data as Season;
  }

  async adminCloseSeason(seasonId: string): Promise<void> {
    await invokeFunction('close-season', { seasonId });
  }

  async adminListUsers(search?: string): Promise<Profile[]> {
    let query = getSupabase().from('profiles').select('*');
    if (search?.trim()) query = query.ilike('alias', `%${search.trim()}%`);
    const { data, error } = await query.order('season_points', { ascending: false }).limit(100);
    if (error) fail(error, 'generic');
    return (data as Profile[] | null) ?? [];
  }

  async adminUpdateUser(input: AdminUserUpdate): Promise<Profile> {
    return invokeFunction<Profile>('admin-update-user', {
      ...input,
    } as unknown as Record<string, unknown>);
  }

  async adminGrantBadge(userId: string, badgeId: string): Promise<void> {
    await invokeFunction('admin-grant-badge', { userId, badgeId });
  }

  async adminRevokeBadge(userBadgeId: string): Promise<void> {
    await invokeFunction('admin-revoke-badge', { userBadgeId });
  }

  async adminGetWeightClasses(): Promise<WeightClassThresholds> {
    const { data, error } = await getSupabase()
      .from('app_settings')
      .select('value')
      .eq('key', 'weight_classes')
      .single();
    if (error) fail(error, 'generic');
    return (data as { value: WeightClassThresholds }).value;
  }

  async adminUpdateWeightClasses(input: WeightClassThresholds): Promise<void> {
    const { error } = await getSupabase()
      .from('app_settings')
      .update({ value: input })
      .eq('key', 'weight_classes');
    if (error) fail(error, 'generic');
  }

  async adminGetMetrics(): Promise<AdminMetrics> {
    const { data, error } = await getSupabase().rpc('get_admin_metrics');
    if (error) fail(error, 'generic');
    return data as AdminMetrics;
  }

  async adminListAuditLog(limit = 50): Promise<AuditLogEntry[]> {
    const { data, error } = await getSupabase()
      .from('audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) fail(error, 'generic');
    return (data as AuditLogEntry[] | null) ?? [];
  }

  async adminGetComments(ids: string[]): Promise<SubmissionComment[]> {
    if (ids.length === 0) return [];
    const { data, error } = await getSupabase()
      .from('submission_comments')
      .select(COMMENT_SELECT)
      .in('id', ids);

    if (error) fail(error, 'generic');
    return (data ?? []).map(toComment);
  }

  async adminSetCommentHidden(commentId: string, hidden: boolean): Promise<void> {
    const me = await this.requireUserId();
    const { error } = await getSupabase()
      .from('submission_comments')
      .update({
        hidden_at: hidden ? new Date().toISOString() : null,
        hidden_by: hidden ? me : null,
      })
      .eq('id', commentId);

    if (error) fail(error, 'generic');
  }

  async adminSetSubmissionHidden(submissionId: string, hidden: boolean): Promise<void> {
    // Por funcion y no por UPDATE: asi este boton solo puede tocar la
    // visibilidad, nunca la puntuacion ni el estado (migracion 20260101001900).
    const { error } = await getSupabase().rpc('set_submission_hidden', {
      p_submission: submissionId,
      p_hidden: hidden,
    });
    if (error) fail(error, 'forbidden');
  }

  async adminGetForumPosts(ids: string[]): Promise<ForumPost[]> {
    if (ids.length === 0) return [];
    const { data, error } = await getSupabase()
      .from('forum_posts')
      .select(FORUM_POST_SELECT)
      .in('id', ids);

    if (error) fail(error, 'generic');
    return (data ?? []).map(toForumPost);
  }

  async adminSetForumPostHidden(postId: string, hidden: boolean): Promise<void> {
    const { error } = await getSupabase().rpc('set_forum_post_hidden', {
      p_post: postId,
      p_hidden: hidden,
    });
    if (error) fail(error, 'forbidden');
  }

  async adminResolveReport(reportId: string): Promise<void> {
    const me = await this.requireUserId();
    const { error } = await getSupabase()
      .from('reports')
      .update({ status: 'CLOSED', resolved_by: me })
      .eq('id', reportId);

    if (error) fail(error, 'generic');
  }

  async adminTranslate(text: string, from: Language, to: Language): Promise<string> {
    // La traduccion la hace una Edge Function: desde el navegador la politica
    // de seguridad del sitio solo deja hablar con Supabase, y asi el servicio
    // de traduccion no queda abierto a cualquiera.
    const result = await invokeFunction<{ text: string }>('translate', { text, from, to });
    return result.text;
  }

  async adminListReports(): Promise<Report[]> {
    const { data, error } = await getSupabase()
      .from('reports')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) fail(error, 'generic');
    return (data as Report[] | null) ?? [];
  }
}
